import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { format } from 'date-fns';

// Shown instead of the page to anyone without Stock Take access.
export const NoStockTakeAccess = () => (
  <div style={{ maxWidth: '760px' }}>
    <div className="page-header">
      <div>
        <h1 className="page-title">Stock Take</h1>
      </div>
    </div>
    <div className="card">
      <div className="empty-state">
        <p>You do not have access to Stock Take. Ask an admin to switch it on.</p>
      </div>
    </div>
  </div>
);

const StockTake = () => {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ part_number: '', description: '', location: '', quantity: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [noAccess, setNoAccess] = useState(false);
  const partRef = useRef(null);
  // Clearing takes two confirmations: step 1 asks, step 2 needs CLEAR typed.
  const [clearStep, setClearStep] = useState(0);
  const [clearText, setClearText] = useState('');
  const [clearing, setClearing] = useState(false);

  // The server re-checks access on every request; if it has just been switched
  // off, show the plain message rather than an error.
  const denied = (err) => {
    if (err.response?.status !== 403) return false;
    setNoAccess(true);
    return true;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/stock');
      setEntries(res.data);
    } catch (err) {
      if (!denied(err)) setError('Failed to load stock take entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const partNumber = form.part_number.trim();
    if (!partNumber) return setError('Please enter a part number');
    const qty = Number(form.quantity);
    if (form.quantity === '' || !Number.isFinite(qty) || qty < 0) {
      return setError('Please enter a quantity of zero or more');
    }

    setSaving(true);
    setError('');
    try {
      const res = await api.post('/api/stock', {
        part_number: partNumber,
        description: form.description.trim(),
        location: form.location.trim(),
        quantity: qty,
      });
      setEntries(prev => [res.data, ...prev]);
      // Keep the location: people usually count several parts in one place.
      setForm(prev => ({ part_number: '', description: '', location: prev.location, quantity: '' }));
      setSuccess('Added');
      setTimeout(() => setSuccess(''), 2000);
      if (partRef.current) partRef.current.focus();
    } catch (err) {
      if (!denied(err)) setError(err.response?.data?.error || 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this entry?')) return;
    try {
      await api.delete(`/api/stock/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      if (!denied(err)) setError(err.response?.data?.error || 'Failed to remove entry');
    }
  };

  const handleExport = async () => {
    setError('');
    try {
      const res = await api.get('/api/stock/export', { responseType: 'blob' });
      // Prefer the filename the server sends, fall back to a sensible default
      let filename = `tranzparts-stock-take-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      const disp = res.headers['content-disposition'];
      const match = disp && disp.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];

      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (!denied(err)) setError('Failed to export');
    }
  };

  const closeClear = () => { setClearStep(0); setClearText(''); };

  const handleClear = async () => {
    setClearing(true);
    setError('');
    try {
      const res = await api.post('/api/stock/clear', { confirm: clearText.trim() });
      setEntries([]);
      closeClear();
      setSuccess(`Stock take cleared (${res.data.cleared} entries)`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear the list');
      closeClear();
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return '-'; }
  };

  if (noAccess) return <NoStockTakeAccess />;

  return (
    <div style={{ maxWidth: '760px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Stock Take</h1>
          <p className="page-subtitle">Your own list: enter each part, where it is and how many you count. Nobody else sees it.</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Part Number</label>
              <input
                type="text"
                name="part_number"
                className="form-control"
                value={form.part_number}
                onChange={handleChange}
                ref={partRef}
                autoFocus
                autoComplete="off"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <input
                type="text"
                name="description"
                className="form-control"
                value={form.description}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Location (optional)</label>
              <input
                type="text"
                name="location"
                className="form-control"
                value={form.location}
                onChange={handleChange}
                maxLength={120}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input
                type="number"
                name="quantity"
                className="form-control"
                value={form.quantity}
                onChange={handleChange}
                min="0"
                step="any"
                inputMode="decimal"
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '13px 18px' }} disabled={saving}>
              {saving ? 'Adding...' : 'Add to stock take'}
            </button>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">My entries ({entries.length})</h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={handleExport} disabled={entries.length === 0}>
              Export to spreadsheet
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => setClearStep(1)} disabled={entries.length === 0}>
              Clear my list
            </button>
          </div>
        </div>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="empty-state">
            <h3>No entries yet</h3>
            <p>Add your first part above to start the stock take</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Part Number</th>
                  <th>Description</th>
                  <th>Location</th>
                  <th>Quantity</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 500 }}>{e.part_number}</td>
                    <td style={{ color: 'var(--grey-mid)' }}>{e.description || '-'}</td>
                    <td style={{ color: 'var(--grey-mid)' }}>{e.location || '-'}</td>
                    <td>{e.quantity}</td>
                    <td style={{ color: 'var(--grey-mid)' }}>{formatDate(e.created_at)}</td>
                    <td>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {clearStep > 0 && (
        <div className="modal-overlay" onClick={closeClear}>
          <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2 className="modal-title">{clearStep === 1 ? 'Clear your stock take list?' : 'Are you sure?'}</h2>
              <button className="modal-close" onClick={closeClear} aria-label="Close">&times;</button>
            </div>
            {clearStep === 1 ? (
              <>
                <div className="modal-body">
                  <p>This removes all {entries.length} of your entries from your list and your export. Nobody else's list is touched.</p>
                  <p style={{ marginTop: 10, color: 'var(--grey-mid)' }}>Export first if you need a copy of this count.</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline" onClick={handleExport}>Export first</button>
                  <button className="btn btn-outline" onClick={closeClear}>Cancel</button>
                  <button className="btn btn-red" onClick={() => setClearStep(2)}>Continue</button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-body">
                  <label className="form-label" htmlFor="clear-confirm">Type CLEAR to confirm</label>
                  <input
                    id="clear-confirm"
                    className="form-control"
                    value={clearText}
                    onChange={e => setClearText(e.target.value)}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline" onClick={closeClear}>Cancel</button>
                  <button className="btn btn-red" onClick={handleClear} disabled={clearText.trim() !== 'CLEAR' || clearing}>
                    {clearing ? 'Clearing...' : 'Clear my list'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTake;
